import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { GraduationCap, Plus, Trash2, Paperclip, Loader2, X, Save, AlertTriangle } from 'lucide-react';
import {
  fetchTitulaciones, crearTitulacion, borrarTitulacion, fetchAdjuntoTitulacion, abrirBlob,
} from '../../../peticiones_API/PeticionesFichaRrhh';

/**
 * Fase 12 · §6.7 — la pestaña de Formación.
 *
 * ── SIN PUERTA, Y ES UNA DECISIÓN ─────────────────────────────────────────────────────────
 *
 * Un título es algo que la gente pone en su currículum: no está en la misma categoría que un IBAN.
 * Se ve con el permiso normal de la ficha, sin segundo factor y sin re-autenticación.
 *
 * **La vecindad en una pantalla no es una clasificación.** Que esté al lado de RRHH no lo
 * convierte en confidencial, y exigir aquí un código sería fricción sin motivo — y la fricción sin
 * motivo enseña a saltársela.
 *
 * ── EL ADJUNTO, POR PROXY ─────────────────────────────────────────────────────────────────
 *
 * Igual que el DNI: el servidor lo transmite, autenticado y sin caché. Lo que se guarda en la base
 * es **el path**, no la URL — sin el path el borrado no es determinista (lección de Fase 8).
 */

const claseInput = 'w-full rounded-v25-md border border-v25-line bg-white px-3 py-2 text-[13px] text-v25-ink-900';

/**
 * Los años de una titulación: desde 1950 y hasta el actual. **Nadie se titula en el futuro.**
 *
 * En desplegable y no en texto libre — admitía «20015» —, de más reciente a más antiguo. El tope
 * se calcula en cada render: escrito a mano empezaría a mentir el 1 de enero.
 *
 * Y el servidor valida lo mismo: el desplegable limita el ratón, no la API.
 */
const ANIOS_TITULACION = (() => {
  const hasta = new Date().getFullYear();
  return Array.from({ length: hasta - 1950 + 1 }, (_, i) => hasta - i);
})();
const VACIO = { titulo: '', tipo: '', centro: '', anio: '' };

export function PestanaFormacion({
  personaId, tipo = null, puedeEditar = false, clave = 'formacion',
  alCambiar = null, registrarGuardado = null,
}) {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [form, setForm] = useState(VACIO);
  const [fichero, setFichero] = useState(null);
  const [abriendoForm, setAbriendoForm] = useState(false);
  const [guardando, setGuardando] = useState(false);

  /**
   * Fase 12 · §6.9 — cambios sin guardar.
   *
   * Aquí no hay «instantánea contra la que comparar» como en `RecursoForm`: lo que se edita es un
   * formulario de ALTA, así que hay cambios sin guardar exactamente cuando algo se ha escrito y
   * todavía no se ha pulsado su botón.
   *
   * El adjunto cuenta: alguien puede elegir el fichero, no rellenar nada más y cambiarse de
   * pestaña. Si sólo mirásemos los campos de texto, ese caso se perdería en silencio.
   */
  const hayCambios = abriendoForm
    && (Object.values(form).some((v) => String(v).trim() !== '') || !!fichero);

  // Se AVISA al armazón, que es quien controla el cambio de pestaña. Allí es un `ref`, así que
  // esto no repinta la ficha entera; aquí sí es estado, porque aquí se pinta el aviso.
  useEffect(() => {
    if (alCambiar) alCambiar(clave, hayCambios);
  }, [alCambiar, clave, hayCambios]);

  // Al desmontar, el aviso deja de tener sentido: la pestaña ya no está. Sin esto, la bandera se
  // quedaría en `true` y la ficha avisaría de cambios de un formulario que ya no existe.
  useEffect(() => () => { if (alCambiar) alCambiar(clave, false); }, [alCambiar, clave]);

  // Cerrar la pestaña del navegador o recargar tampoco debería llevarse lo escrito. Es el mismo
  // patrón de `RecursoForm`, y cubre lo que el aviso de pestaña no puede ver.
  useEffect(() => {
    if (!hayCambios) return undefined;
    const alSalir = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', alSalir);
    return () => window.removeEventListener('beforeunload', alSalir);
  }, [hayCambios]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setFilas(await fetchTitulaciones(personaId, tipo));
    } catch (e) {
      toast.error('No se pudieron cargar las titulaciones.');
      setFilas([]);
    } finally {
      setCargando(false);
    }
  }, [personaId, tipo]);

  useEffect(() => { if (personaId) cargar(); }, [personaId, cargar]);

  /**
   * Fase 12 · §6.10 — la llama el «Guardar» de la ficha, no un botón propio.
   *
   * **Devuelve `true` si guardó y `false` si falló**, para que «Guardar y Salir» no salga tras un
   * error y se lleve por delante la titulación escrita.
   *
   * Sin formulario abierto no hay nada que guardar y devuelve `true`: pulsar «Guardar y Salir» en
   * una pestaña intacta tiene que salir. Pero un formulario abierto **con el título vacío sí es
   * un error** —es el único campo obligatorio—, así que ahí devuelve `false` y no se sale: lo
   * contrario tiraría a la basura el centro y el año que sí se habían escrito.
   */
  const crear = async () => {
    if (!abriendoForm) return true;
    if (!form.titulo.trim()) { toast.warn('El título es obligatorio.'); return false; }
    setGuardando(true);
    try {
      const nueva = await crearTitulacion(personaId, form, fichero, tipo);
      setFilas((f) => [nueva, ...f]);
      setForm(VACIO); setFichero(null); setAbriendoForm(false);
      toast.success('Titulación añadida.');
      return true;
    } catch (e) {
      // El mensaje del servidor, no uno propio: dice cuál de las validaciones falló.
      toast.error(e?.response?.data?.message || 'No se pudo añadir la titulación.');
      return false;
    } finally {
      setGuardando(false);
    }
  };

  // Se registra en el armazón. Envoltorio estable sobre una referencia que se refresca en cada
  // render: registrar `crear` directamente obligaría a re-registrar con cada tecla, y registrarlo
  // una sola vez congelaría la versión del primer render, que ve el formulario cerrado y no
  // guardaría nunca.
  const crearRef = useRef(null);
  crearRef.current = crear;
  useEffect(() => {
    if (!registrarGuardado) return undefined;
    registrarGuardado(clave, () => crearRef.current());
    return () => registrarGuardado(clave, null);
  }, [registrarGuardado, clave]);

  const borrar = async (id) => {
    try {
      const r = await borrarTitulacion(personaId, id, tipo);
      setFilas((f) => f.filter((x) => x.id !== id));
      // Se dice si el adjunto se fue con ella: un fichero huérfano no se ve desde ninguna pantalla.
      toast.success(r?.adjunto_borrado ? 'Titulación y adjunto eliminados.' : 'Titulación eliminada.');
    } catch (e) {
      toast.error('No se pudo eliminar.');
    }
  };

  const verAdjunto = async (id) => {
    try {
      abrirBlob(await fetchAdjuntoTitulacion(personaId, id, tipo));
    } catch (e) {
      toast.error('No se pudo abrir el adjunto.');
    }
  };

  return (
    <section className="rounded-v25-lg border border-v25-line bg-white p-4">
      <header className="mb-3 flex items-center justify-between border-b border-v25-line pb-2">
        <div className="flex items-center gap-2">
          <GraduationCap size={17} className="text-v25-brand-600" />
          <h4 className="text-[14px] font-semibold text-v25-ink-900">
            Titulaciones {filas.length ? `(${filas.length})` : ''}
          </h4>
        </div>
        {puedeEditar && !abriendoForm && (
          <button type="button" onClick={() => setAbriendoForm(true)}
            className="inline-flex items-center gap-1 rounded-v25-md border border-v25-line bg-white px-3 py-1.5 text-[13px] font-semibold text-v25-ink-700 shadow-v25-btn">
            <Plus size={14} /> Añadir
          </button>
        )}
      </header>

      {abriendoForm && (
        <div className="mb-4 rounded-v25-md border border-v25-line bg-v25-surface-sunken p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[13px] font-semibold text-v25-ink-900">Nueva titulación</span>
            <button type="button" onClick={() => { setAbriendoForm(false); setForm(VACIO); setFichero(null); }}
              aria-label="Cancelar" className="text-v25-ink-500"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <input className={claseInput} placeholder="Título *" value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
            <input className={claseInput} placeholder="Tipo (Grado, Máster, Certificado…)" value={form.tipo}
              onChange={(e) => setForm({ ...form, tipo: e.target.value })} />
            <input className={claseInput} placeholder="Centro" value={form.centro}
              onChange={(e) => setForm({ ...form, centro: e.target.value })} />
            <select className={claseInput} aria-label="Año" value={form.anio}
              onChange={(e) => setForm({ ...form, anio: e.target.value })}>
              <option value="">Año</option>
              {ANIOS_TITULACION.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={(e) => setFichero(e.target.files?.[0] || null)}
              className="text-[12px] text-v25-ink-700" />
            {/* Fase 12 · §6.10 — SIN BOTÓN PROPIO: lo guarda el «Guardar» de la ficha.
                Un formulario de alta sin ningún botón parecería roto, así que en su sitio va
                dónde está el que lo guarda. El aviso de «Sin guardar» es el de aquí; el del
                cambio de pestaña es la red, y esto es para que no haga falta la red. */}
            <span className="inline-flex items-center gap-1.5 text-[12px] text-v25-ink-500">
              <Save size={14} />
              Se añade con <strong className="font-semibold text-v25-ink-700">Guardar</strong> al
              final de la ficha.
            </span>
            {guardando && (
              <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium text-v25-ink-700">
                <Loader2 size={14} className="animate-spin" /> Guardando…
              </span>
            )}
            {!guardando && hayCambios && (
              <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium text-v25-ink-500">
                <AlertTriangle size={14} /> Sin guardar
              </span>
            )}
          </div>
          <p className="mt-2 text-[12px] text-v25-ink-500">
            El adjunto admite PDF e imágenes, hasta 10&nbsp;MB. Se comprueba el tipo real del
            fichero, no su extensión.
          </p>
        </div>
      )}

      {cargando && (
        <div className="flex items-center gap-2 p-6 text-[13px] text-v25-ink-500">
          <Loader2 size={16} className="animate-spin" /> Cargando…
        </div>
      )}

      {!cargando && !filas.length && (
        <p className="rounded-v25-md border border-dashed border-v25-line px-3 py-3 text-[12px] text-v25-ink-500">
          Sin titulaciones registradas.
        </p>
      )}

      {!cargando && filas.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-v25-line text-left text-[12px] font-semibold text-v25-ink-700">
                <th className="py-2 pr-3">Título</th>
                <th className="py-2 pr-3">Tipo</th>
                <th className="py-2 pr-3">Centro</th>
                <th className="py-2 pr-3">Año</th>
                <th className="py-2 pr-3">Adjunto</th>
                {puedeEditar && <th className="py-2" aria-label="Acciones" />}
              </tr>
            </thead>
            <tbody>
              {filas.map((t) => (
                <tr key={t.id} className="border-b border-v25-line last:border-0">
                  <td className="py-2 pr-3 text-v25-ink-900">{t.titulo}</td>
                  <td className="py-2 pr-3 text-v25-ink-700">{t.tipo || '—'}</td>
                  <td className="py-2 pr-3 text-v25-ink-700">{t.centro || '—'}</td>
                  <td className="py-2 pr-3 text-v25-ink-700">{t.anio || '—'}</td>
                  <td className="py-2 pr-3">
                    {t.tiene_adjunto ? (
                      <button type="button" onClick={() => verAdjunto(t.id)}
                        className="inline-flex items-center gap-1 text-v25-brand-600 hover:underline">
                        <Paperclip size={14} /> Ver
                      </button>
                    ) : <span className="text-v25-ink-400">—</span>}
                  </td>
                  {puedeEditar && (
                    <td className="py-2 text-right">
                      <button type="button" onClick={() => borrar(t.id)}
                        aria-label={`Eliminar ${t.titulo}`}
                        className="text-v25-ink-500 hover:text-v25-expired-fg">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-[12px] leading-relaxed text-v25-ink-500">
        No es dato confidencial de RRHH: se ve con el permiso normal de la ficha, sin verificación
        en dos pasos.
      </p>
    </section>
  );
}

export default PestanaFormacion;
